import { signInWithCustomToken } from "firebase/auth";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { registerUser } from "~/lib/api";
import { firebaseAuth } from "~/lib/firebase";
import { ROLE_OPTIONS, type UserRoleValue } from "~/lib/roles";
import { previewInvitation } from "~/lib/ward-invitations";

interface FormState {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  role: UserRoleValue | "";
  gmcNumber: string;
}

const INITIAL_FORM: FormState = {
  email: "",
  password: "",
  confirmPassword: "",
  fullName: "",
  role: "",
  gmcNumber: "",
};

interface FieldErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  fullName?: string;
  role?: string;
  gmcNumber?: string;
}

// RFC 7807 problem+json shape returned by our HttpExceptionFilter.
interface ProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  errors?: string[];
}

const GMC_PATTERN = /^\d{7}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.email) errors.email = "Required";
  else if (!EMAIL_PATTERN.test(form.email)) errors.email = "Enter a valid email";
  if (!form.password) errors.password = "Required";
  else if (form.password.length < 12) errors.password = "Must be at least 12 characters";
  if (!form.confirmPassword) errors.confirmPassword = "Required";
  else if (form.confirmPassword !== form.password)
    errors.confirmPassword = "Passwords do not match";
  if (!form.fullName.trim()) errors.fullName = "Required";
  if (!form.role) errors.role = "Required";
  if (!form.gmcNumber) errors.gmcNumber = "Required";
  else if (!GMC_PATTERN.test(form.gmcNumber)) errors.gmcNumber = "Must be exactly 7 digits";
  return errors;
}

// Simple zxcvbn-lite: length is the dominant signal, character variety bumps
// the score. Returns 0–4 so the strength bar renders 5 buckets.
function scorePassword(pw: string): number {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (pw.length >= 16) score += 1;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z0-9]/].filter((re) => re.test(pw)).length;
  if (classes >= 3) score += 1;
  return Math.min(score, 4);
}

// Invite-link state for the invited-register flow (V2-E3.3). An invite link
// (`/register?invite=TOKEN`, emailed by E3.2) is previewed on mount: a live
// pending invite pre-fills and locks the email and shows a "you've been invited"
// banner so registering with that address auto-joins the ward (redeem happens
// server-side, keyed on the email, inside the registration transaction). A spent
// or unknown token degrades to ordinary registration with a gentle notice.
type InviteState =
  | { kind: "none" }
  | { kind: "loading" }
  | { kind: "valid"; wardName: string; email: string }
  | { kind: "invalid"; message: string };

const STRENGTH_LABELS = ["Very weak", "Weak", "Fair", "Strong", "Very strong"] as const;
const STRENGTH_COLORS = [
  "bg-destructive",
  "bg-destructive",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-emerald-600",
] as const;

export default function RegisterRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState<InviteState>(
    inviteToken ? { kind: "loading" } : { kind: "none" },
  );

  // Resolve the invite token to its preview, pre-filling + locking the email so
  // the redeem-on-register email match is guaranteed.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    void previewInvitation(inviteToken).then((result) => {
      if (cancelled) return;
      if (result.status === "error") {
        setInvite({ kind: "invalid", message: result.message });
        return;
      }
      const preview = result.data;
      if (preview.status !== "pending") {
        setInvite({
          kind: "invalid",
          message:
            preview.status === "accepted"
              ? "This invitation has already been accepted. Log in instead."
              : "This invitation link has expired or been revoked. You can still create an account.",
        });
        return;
      }
      setInvite({ kind: "valid", wardName: preview.wardName, email: preview.email });
      setForm((prev) => ({ ...prev, email: preview.email }));
    });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const emailLocked = invite.kind === "valid";

  const passwordScore = useMemo(() => scorePassword(form.password), [form.password]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    setFormError(null);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const errors = validate(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await registerUser({
        body: {
          email: form.email.trim(),
          password: form.password,
          fullName: form.fullName.trim(),
          role: form.role as UserRoleValue,
          gmcNumber: form.gmcNumber,
        },
      });

      if (result.error || !result.data) {
        const problem = (result.error ?? {}) as ProblemDetails;
        applyServerErrors(problem, setFieldErrors, setFormError);
        return;
      }

      await signInWithCustomToken(firebaseAuth, result.data.customToken);
      navigate("/", { replace: true });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-svh items-center justify-center bg-background px-6 py-12 text-foreground">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border bg-card p-6 shadow-sm">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Create your account</h1>
          <p className="text-sm text-muted-foreground">
            HandoverPro is for clinical staff. Your GMC number is stored against your account.
          </p>
        </header>

        {invite.kind === "valid" && (
          <div
            role="status"
            className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-foreground"
          >
            You’ve been invited to join <span className="font-medium">{invite.wardName}</span>.
            Register with this email to accept.
          </div>
        )}

        {invite.kind === "invalid" && (
          <div
            role="status"
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-foreground"
          >
            {invite.message}
          </div>
        )}

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <Field id="fullName" label="Full name" error={fieldErrors.fullName}>
            <Input
              id="fullName"
              autoComplete="name"
              value={form.fullName}
              onChange={(e) => update("fullName", e.target.value)}
              aria-invalid={Boolean(fieldErrors.fullName)}
              required
            />
          </Field>

          <Field id="email" label="Email" error={fieldErrors.email}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              aria-invalid={Boolean(fieldErrors.email)}
              // Locked to the invited address so the redeem-on-register email
              // match holds; a normal sign-up leaves it editable.
              readOnly={emailLocked}
              aria-readonly={emailLocked || undefined}
              className={
                emailLocked ? "cursor-not-allowed bg-muted text-muted-foreground" : undefined
              }
              required
            />
          </Field>

          <Field id="role" label="Role" error={fieldErrors.role}>
            <select
              id="role"
              value={form.role}
              onChange={(e) => update("role", e.target.value as UserRoleValue | "")}
              aria-invalid={Boolean(fieldErrors.role)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring aria-[invalid=true]:border-destructive"
              required
            >
              <option value="" disabled>
                Select a role…
              </option>
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          <Field id="gmcNumber" label="GMC number" error={fieldErrors.gmcNumber}>
            <Input
              id="gmcNumber"
              inputMode="numeric"
              pattern="\d{7}"
              autoComplete="off"
              maxLength={7}
              value={form.gmcNumber}
              onChange={(e) => update("gmcNumber", e.target.value.replace(/\D/g, ""))}
              aria-invalid={Boolean(fieldErrors.gmcNumber)}
              placeholder="7 digits"
              required
            />
          </Field>

          <Field id="password" label="Password" error={fieldErrors.password}>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              aria-invalid={Boolean(fieldErrors.password)}
              required
            />
            <PasswordStrength score={passwordScore} hasInput={form.password.length > 0} />
          </Field>

          <Field id="confirmPassword" label="Confirm password" error={fieldErrors.confirmPassword}>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={(e) => update("confirmPassword", e.target.value)}
              aria-invalid={Boolean(fieldErrors.confirmPassword)}
              required
            />
          </Field>

          {formError && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {formError}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Creating account…" : "Create account"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </main>
  );
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function PasswordStrength({ score, hasInput }: { score: number; hasInput: boolean }): JSX.Element {
  return (
    <div className="space-y-1 pt-1" aria-live="polite">
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full ${
              hasInput && i <= score ? STRENGTH_COLORS[score] : "bg-muted"
            }`}
          />
        ))}
      </div>
      {hasInput && (
        <p className="text-xs text-muted-foreground">Strength: {STRENGTH_LABELS[score]}</p>
      )}
    </div>
  );
}

function applyServerErrors(
  problem: ProblemDetails,
  setFieldErrors: (errs: FieldErrors) => void,
  setFormError: (msg: string | null) => void,
): void {
  if (Array.isArray(problem.errors) && problem.errors.length > 0) {
    const next: FieldErrors = {};
    for (const message of problem.errors) {
      const lower = message.toLowerCase();
      if (lower.includes("email")) next.email = message;
      else if (lower.includes("password")) next.password = message;
      else if (lower.includes("gmc")) next.gmcNumber = message;
      else if (lower.includes("role")) next.role = message;
      else if (lower.includes("name")) next.fullName = message;
    }
    if (Object.keys(next).length > 0) {
      setFieldErrors(next);
      return;
    }
  }
  setFormError(problem.title ?? problem.detail ?? "Registration failed.");
}
